import './index.scss';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../../shared/Datepicker';
import React, { useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import PhoneInput from '../../../shared/PhoneInput/PhoneInput';
import { Col, FormFeedback, Input, Label, Row } from 'reactstrap';
import {
  useFetchMyCompanyUsers,
  useCompanyManagementUsers,
} from '../../../../store/CompanyManagement/companyManagementHooks';
import { useMyCompany, useProfile } from '../../../../store/Auth/authHooks';
import { emailValidation } from '../../../../utility/functions/emailValidation';
import { getGuestSpecialNeedOptions } from '../../../../utility/functions/get-guest-special-need-options';
import { SpecialNeedEnum } from '../../../../API/guest-management/enum/special-need.enum';
import { useGetLocation } from '../../../../store/RoomManagement/resourceManagementHooks';
import { useSpacesManagementCompanySpaces } from '../../../../store/SpacesManagement/spacesManagementHooks';

type PropsTypes = {
  form: UseFormReturn<any, object>;
  isDuplicate?: boolean;
};

export const InviteGuestForm = (props: PropsTypes) => {
  const profile = useProfile();
  const location = useGetLocation();
  const { t } = useTranslation();
  const myCompany = useMyCompany();
  const users = useCompanyManagementUsers();
  const fetchUsers = useFetchMyCompanyUsers();
  const companySpaces = useSpacesManagementCompanySpaces();

  const spaceOptions = companySpaces.map((space) => {
    return {
      value: space.id,
      label: `Building : ${space.building.name}, floor : ${space.floor?.number}, space : ${space.name}`,
    };
  });

  useEffect(() => {
    fetchUsers().finally();
  }, []);

  const {
    watch,
    control,
    getValues,
    formState: { errors },
  } = props.form;

  const cardIdValidation = (value?: string): any => {
    return (
      !!value ||
      !getValues('allowParking') ||
      t('FORM_ERRORS_FIELD_IS_REQUIRED')
    );
  };

  const fullNameValidation = (value?: string): any => {
    const trimValue = value?.trim();
    return (
      trimValue?.replace(' ', '').length !== trimValue?.length ||
      t('FULL_NAME_VALIDATION')
    );
  };

  const guestContactInformationRequired = () => {
    return (
      !!props.form.watch('email') ||
      !!props.form.watch('phone') ||
      t('FORM_ERRORS_FIELD__OR_FIELD_IS_REQUIRED', {
        field1: t('FORM_EMAIL_PLACEHOLDER'),
        field2: t('FORM_PHONE_NUMBER_PLACEHOLDER'),
      })
    );
  };

  const usersOptions = [
    { value: '', label: t('FREE_TEXT_OPTION') },
    ...users.map((user) => {
      return { value: user!.fullName!, label: `${user!.fullName!}` };
    }),
  ];

  const invitedNameValidation = (value: any): boolean => {
    return !!(value || watch('invitedNameFreeText'));
  };

  const specialNeedsOptions = [
    {
      label: t('NO_NEEDED'),
      value: SpecialNeedEnum.NORMAL,
    },
    ...getGuestSpecialNeedOptions(),
  ];

  return (
    <>
      <Row>
        <Col md="6" className="form-user-input">
          <Label for="fullName">
            <span>* </span>
            {t('VISITOR_NAME_LABEL')}
          </Label>
          <Controller
            name="fullName"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
              pattern: {
                value: /^[0-9a-zA-Z _"'.\u0590-\u05FF\u200f\u200e]*$/,
                message: t('FORM_ERRORS_INVALID_USE_LETTERS_NUMBER'),
              },
              validate: {
                fullName: (e) => fullNameValidation(e),
              },
            }}
            render={({ field }) => (
              <Input
                id="fullName"
                autoComplete="off"
                type="text"
                className="text-input"
                placeholder={t('NAME_LABEL')}
                invalid={errors.fullName && true}
                {...field}
              />
            )}
          />
          {errors.fullName ? (
            <FormFeedback className="form-input-error">
              {errors.fullName.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <Label for="arrivingOn">
            <span>* </span>
            {t('DATE_LABEL')}
          </Label>
          <Controller
            name="arrivingOn"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
              },
            }}
            render={({ field }) => (
              <DatePicker
                id="arrivingOn"
                className="text-input"
                placeholder={t('DATE_LABEL')}
                minDate={'today'}
                {...field}
              />
            )}
          />
          {errors.arrivingOn ? (
            <FormFeedback className="form-input-error">
              {errors.arrivingOn.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <Label for="email">{t('VISITOR_EMAIL_LABEL')}</Label>
          <Controller
            name="email"
            control={control}
            rules={{
              validate: {
                requiredPatter: () => guestContactInformationRequired(),
                emailPattern: (e) =>
                  emailValidation(e, t('FORM_ERRORS_INVALID_EMAIL'), true),
              },
            }}
            render={({ field }) => (
              <Input
                id="email"
                autoFocus
                type="email"
                placeholder={t('VISITOR_EMAIL_LABEL')}
                invalid={errors.email && true}
                {...field}
              />
            )}
          />
          {errors.email ? (
            <FormFeedback className="form-input-error">
              {errors.email.message}
            </FormFeedback>
          ) : null}
        </Col>
        <Col md="6" className="form-user-input">
          <PhoneInput
            label={t('VISITOR_PHONE_LABEL')}
            placeholder={t('VISITOR_PHONE_LABEL')}
            control={control}
            form={props.form}
            controlName={'phone'}
            rules={{
              validate: {
                requiredPatter: () => guestContactInformationRequired(),
              },
            }}
          />
        </Col>
        <Col md="6" className="form-user-input">
          <Label for="specialNeed"> {t('SPECIAL_NEEDS_TEXT')}</Label>
          <Controller
            control={control}
            defaultValue={specialNeedsOptions.map((c) => c.value)}
            name="specialNeed"
            render={({ field: { onChange, value } }) => (
              <Select
                id="specialNeed"
                value={specialNeedsOptions.filter((c) => value === c.value)}
                onChange={(val) => onChange(val?.value)}
                options={specialNeedsOptions}
                classNamePrefix="select"
              />
            )}
          />
          {errors.invitedName ? (
            <FormFeedback className="form-input-error">
              {t('INVITER_NAME_ERROR')}
            </FormFeedback>
          ) : null}
        </Col>
        {spaceOptions.length > 1 && (
          <Col md="12" className="form-user-input">
            <Label for="space">
              <span>* </span>
              {t('PLACE')}
            </Label>
            <Controller
              control={props.form.control}
              rules={{
                required: {
                  value: true,
                  message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                },
              }}
              name="space"
              render={({ field: { onChange, value } }) => (
                <Select
                  value={spaceOptions.filter((c) => +value === +c.value)}
                  id="space"
                  onChange={(val) => {
                    onChange(val?.value);
                  }}
                  options={spaceOptions}
                  classNamePrefix="select"
                />
              )}
            />
            {errors.space ? (
              <FormFeedback className="form-input-error">
                {t('FORM_ERRORS_FIELD_IS_REQUIRED')}
              </FormFeedback>
            ) : null}
          </Col>
        )}
        <Col md="12" className="form-user-input">
          <Label for="message">{t('MESSAGE')}</Label>
          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <Input
                id="message"
                type="textarea"
                maxLength={270}
                className="guest-message-textarea"
                invalid={errors.message && true}
                {...field}
              />
            )}
          />
          {errors.message ? (
            <FormFeedback className="form-input-error">
              {errors.message.message}
            </FormFeedback>
          ) : null}
        </Col>
      </Row>
      {myCompany?.lprCompanyId && location.lprType ? (
        <Row>
          {profile.permissions?.guestVehicle && (
            <Col md="12" className="form-switches form-user-input">
              <div className="form-check form-check-inputs">
                <Label for="allowParking">{t('VISITOR_CAR_LABEL')}</Label>
                <Controller
                  name="allowParking"
                  control={control}
                  rules={{
                    required: {
                      value: false,
                      message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                    },
                  }}
                  render={({ field }) => (
                    // @ts-ignore
                    <Input
                      checked={getValues().allowParking}
                      autoComplete="off"
                      id="allowParking"
                      type="checkbox"
                      invalid={errors.allowParking && true}
                      {...field}
                    />
                  )}
                />
                {errors.allowParking ? (
                  <FormFeedback className="form-input-error">
                    {errors.allowParking.message}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          )}
          {watch('allowParking') && (
            <>
              <Col md="4" className="form-user-input">
                <Label for="licenseNumber">
                  {t('VISITOR_CAR_NUMBER_LABEL')}
                </Label>
                <Controller
                  name="licenseNumber"
                  control={control}
                  rules={{
                    validate: {
                      required: cardIdValidation,
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      disabled={!watch('allowParking')}
                      id="licenseNumber"
                      autoComplete="off"
                      type="number"
                      className="text-input"
                      invalid={errors.licenseNumber && true}
                      {...field}
                    />
                  )}
                />
                {errors.licenseNumber ? (
                  <FormFeedback className="form-input-error">
                    {errors.licenseNumber.message}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md="4" className="form-user-input">
                <Label for="parkingFloorNumber">
                  {t('VISITOR_PARKING_FLOOR_NUMBER_LABEL')}
                </Label>
                <Controller
                  name="parkingFloorNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      disabled={!watch('allowParking')}
                      id="parkingFloorNumber"
                      autoComplete="off"
                      type="text"
                      className="text-input"
                      invalid={errors.parkingFloorNumber && true}
                      {...field}
                    />
                  )}
                />
                {errors.parkingFloorNumber ? (
                  <FormFeedback className="form-input-error">
                    {errors.parkingFloorNumber.message}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md="4" className="form-user-input">
                <Label for="parkingNumber">
                  {t('VISITOR_PARKING_NUMBER_LABEL')}
                </Label>
                <Controller
                  name="parkingNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      disabled={!watch('allowParking')}
                      id="parkingNumber"
                      autoComplete="off"
                      type="text"
                      className="text-input"
                      invalid={errors.parkingNumber && true}
                      {...field}
                    />
                  )}
                />
                {errors.parkingNumber ? (
                  <FormFeedback className="form-input-error">
                    {errors.parkingNumber.message}
                  </FormFeedback>
                ) : null}
              </Col>
            </>
          )}
        </Row>
      ) : (
        <div></div>
      )}
      <Row>
        <Col md="12" className="form-switches form-user-input">
          <div className="form-check form-check-inputs">
            <Label for="orderForAnother">
              {t('VISITOR_ORDER_FOR_ANOTHER_PERSON_LABEL')}
            </Label>
            <Controller
              name="orderForAnother"
              control={control}
              rules={{
                required: {
                  value: false,
                  message: t('FORM_ERRORS_FIELD_IS_REQUIRED'),
                },
              }}
              render={({ field }) => (
                // @ts-ignore
                <Input
                  checked={getValues().orderForAnother}
                  autoComplete="off"
                  id="orderForAnother"
                  type="checkbox"
                  invalid={errors.orderForAnother && true}
                  {...field}
                />
              )}
            />
            {errors.orderForAnother ? (
              <FormFeedback className="form-input-error">
                {errors.orderForAnother.message}
              </FormFeedback>
            ) : null}
          </div>
        </Col>
        {watch('orderForAnother') && (
          <>
            <Col md="5" className="form-user-input">
              <Label for="invitedName">
                {' '}
                {t('VISITOR_ORDERER_NAME_LABEL')}
              </Label>
              <Controller
                control={control}
                defaultValue={usersOptions?.map((c) => c.value)}
                name="invitedName"
                rules={{
                  validate: invitedNameValidation,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    isDisabled={props.form.watch('invitedNameFreeText')}
                    id="invitedName"
                    value={usersOptions.filter((c) => value === c.value)}
                    onChange={(val) => onChange(val?.value)}
                    options={usersOptions}
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.invitedName ? (
                <FormFeedback className="form-input-error">
                  {t('INVITER_NAME_ERROR')}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md="1" className="or-text">
              <span>OR</span>
            </Col>
            <Col md="6" className="form-user-input">
              <Label for="invitedNameFreeText"></Label>
              <Controller
                name="invitedNameFreeText"
                control={control}
                render={({ field }) => (
                  <Input
                    disabled={props.form.watch('invitedName')}
                    autoComplete="off"
                    id="invitedNameFreeText"
                    type="text"
                    className="text-input"
                    placeholder={t('VISITOR_ORDERER_NAME_FREE_TEXT_LABEL')}
                    {...field}
                  />
                )}
              />
              {errors.invitedNameFreeText ? (
                <FormFeedback className="form-input-error">
                  {t('INVITER_NAME_ERROR')}
                </FormFeedback>
              ) : null}
            </Col>
          </>
        )}
      </Row>
    </>
  );
};
